import { useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';

import { Grid } from '@material-ui/core';

import { useIntl } from '../../../../../../_metronic/i18n/customUseIntl';
import RowTextFieldWithMetrics from '../../../../../modules/Farms/Intervention/RowControlledTextFieldWithMetrics';
import {
  avgLiveWeightMins,
} from '../../../../../modules/Farms/validationSchemaRestrictions';
import { UserProfilePrefs } from '../../../../../modules/Helpers/UserProfilePrefs';
import {
  defaultUnits,
  explicitConvertValue,
  unitLong,
} from '../../../../utils/unit-utils';
import { ButtonControlGroup } from '../../../helpers/ButtonControlGroup';
import { DialogContainer } from '../../CommonDataParts/DialogContainer';

import {
  PigFatteningInput,
  PigFatteningIntervention,
} from '../../../../models/Intervention';
import { FormType } from '../../common';
import { PigFatteningInterventionFormDialogProps } from '../CommonDataParts/InterventionTypes';

const PFInterventionInputFormDialog = ({
  formType = FormType.Add,
  stageIndex = 0,
  formVisible = false,
  handleCancel,
  baseline,
}: PigFatteningInterventionFormDialogProps) => {

  const intl = useIntl();
  // TODO Fix when converting to TS
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const userProfile = UserProfilePrefs.getInstance();
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
  const userUOM = userProfile.getUserUnitPrefs();

  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  const barnOutputMassUnit = userUOM?.unitBarnOutputMass
    ? // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      unitLong(userUOM.unitBarnOutputMass as string)
    : 'kg';

  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
  const precision = userProfile.getUnitBarnOutputMassPrecision() as number;
  const currResetValue = useRef<PigFatteningInput>();

  let minAvgLiveWeightMortality = avgLiveWeightMins['Pig fattening'];

  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  if (userUOM.unitBarnOutputMass !== defaultUnits.unitBarnOutputMass) {
    if (minAvgLiveWeightMortality)
      minAvgLiveWeightMortality = Number(
        explicitConvertValue(
          minAvgLiveWeightMortality,
          defaultUnits.unitBarnOutputMass,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          userUOM.unitBarnOutputMass as string
        )?.toFixed(precision)
      );
  }

  const formTitle = intl.formatMessage({
    id: 'SUSTELL.PROCESS.DIALOG.STAGE.ANIMALS.TITLE',
  });

  const fieldItemPrefix = `stages.${stageIndex}.stageData.input`;
  const fc = useFormContext();

  useEffect(() => {
    if (formVisible) {
      currResetValue.current = {
        ...(fc.getValues(fieldItemPrefix) as PigFatteningInput),
      }; // must 'spread' otherwise just reference
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formVisible]);

  const handleResetClick = () => {
    if (currResetValue.current) {
      const resetObject = { ...fc.getValues() } as PigFatteningIntervention;
      const stageData = resetObject?.stages?.[stageIndex]?.stageData;
      if (stageData?.input) {
        stageData.input = {
          ...currResetValue.current,
        };
        fc.reset(resetObject, {
          errors: true,
        });
      }
    }
    handleCancel();
  };

  const input = baseline?.stages?.[stageIndex]?.stageData?.input;

  return (
    <DialogContainer
      formVisible={formVisible}
      handleClose={handleResetClick}
      formTitle={formTitle}
      datasetType="intervention"
      iconCode="PIG_FATTENING_INPUT"
    >
      <Grid container direction="column" spacing={2} justifyContent="space-between">
        <Grid item xs={12}>
          <RowTextFieldWithMetrics
            name={`${fieldItemPrefix}.purchasedPiglets`}
            label={intl.formatMessage({
              id: 'BASELINE.FORM.BARN.PIGS.PURCHASED_PIGLETS',
            })}
            type="number"
            tooltip={intl.formatMessage({
              id: 'INTERVENTION.FORM.BARN.PIGS.PURCHASED_ANIMALS.TOOLTIP',
            })}
            metricUnit="number"
            baseline={input?.purchasedPiglets}
            margin="none"
            disabled={formType === 'view'}
          />
        </Grid>
        <Grid item xs={12}>
          <RowTextFieldWithMetrics
            name={`${fieldItemPrefix}.averageLiveWeight`}
            label={intl.formatMessage({
              id: 'BASELINE.FORM.BARN.PIGS.AVERAGE_LIVEWEIGHT',
            })}
            type="number"
            tooltip=""
            margin="none"
            metricUnit={barnOutputMassUnit}
            precision={precision}
            baseline={input?.averageLiveWeight}
            disabled={formType === 'view'}
          />
        </Grid>
        <Grid item xs={12}>
          <RowTextFieldWithMetrics
            name={`${fieldItemPrefix}.mortalityRate`}
            label={intl.formatMessage({
              id: 'BASELINE.FORM.BARN.PIGS.MORTALITY',
            })}
            type="number"
            tooltip={intl.formatMessage({
              id: 'INTERVENTION.FORM.BARN.PIGS.MORTALITY.TOOLTIP',
            })}
            metricUnit="%"
            margin="none"
            baseline={input?.mortalityRate}
            disabled={formType === 'view'}
            maxAllowedValue={99}
          />
        </Grid>
        <Grid item xs={12}>
          <RowTextFieldWithMetrics
            name={`${fieldItemPrefix}.averageLiveWeightMortalities`}
            label={intl.formatMessage(
              { id: 'INTERVENTION.FORM.BARN.PIGS.AVG_LIVEWEIGHT_MORTALITY' },
              { min: minAvgLiveWeightMortality, unit: barnOutputMassUnit }
            )}
            type="number"
            tooltip={intl.formatMessage(
              {
                id: 'INTERVENTION.FORM.BARN.PIGS.AVG_LIVEWEIGHT_MORTALITY_TOOLTIP',
              },
              { min: minAvgLiveWeightMortality, unit: barnOutputMassUnit }
            )}
            metricUnit={barnOutputMassUnit}
            precision={precision}
            baseline={input?.averageLiveWeightMortalities}
            disabled={formType === 'view'}
            minAllowedValue={minAvgLiveWeightMortality}
          />
        </Grid>
        <Grid item xs={11}>
          <ButtonControlGroup
            cancelHandler={handleResetClick}
            saveHandler={handleCancel}
            saveLabel={intl.formatMessage({ id: 'GENERAL.CONFIRM' })}
          />
        </Grid>
      </Grid>
    </DialogContainer>
  );
};

export default PFInterventionInputFormDialog;
